import {booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {AksjeeierEnkelModell, Epost, MedlemId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {TranslateModule} from "@ngx-translate/core";
import {DeltakerSkjemaType, MøteRedigerDeltakerKontroller} from "web-ng2/moduler/møte/komponenter/møte-rediger-deltaker/møte-rediger-deltaker";
import {MøteRedigerDeltakerGFKontroller} from "web-ng2/moduler/møte/komponenter/møte-rediger-deltaker-gf/møte-rediger-deltaker-gf";
import {AksjeeierRepresentasjonSkjemaType, erRepresentantForAksjeeierAngitt, GeneralforsamlingRedigerRepresentasjonerKontroller, GfRedigerRepresentasjonerSkjemaType, medlemIdEllerInvitasjonId, MedlemInvitasjonsskjemaForAksjeeierSkjemaType} from "web-ng2/moduler/møte/komponenter/generalforsamling-rediger-representasjon/generalforsamling-rediger-representasjoner";
import {map} from "rxjs/operators";
import {skjemaendringerSomObserverbar} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {combineLatest, distinctUntilChanged, Observable} from "rxjs";
import {toSignal} from "@angular/core/rxjs-interop";
import {distinkte} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";

const debug = initDebugger('MøteRedigerDeltakereGeneralforsamlingKontroller');

export type DeltakereGfSkjemaType = ReturnType<typeof MøteRedigerDeltakereGeneralforsamlingKontroller.opprettSkjema>;
export type DeltakereGfSkjemaVerdier = DeltakereGfSkjemaType['value'];
export type Deltaker = { medlem: { id: MedlemId }; skalSignere?: boolean };

@Component({
    selector: 'moete-rediger-deltakere-gf',
    templateUrl: './møte-rediger-deltakere-gf.html',
    styleUrls: ['./møte-rediger-deltakere-gf.scss'],
    imports: [
        NgClass,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        TipsKomponent,
        MatSlideToggleModule,
        TranslateModule,
        MøteRedigerDeltakerKontroller,
        MøteRedigerDeltakerGFKontroller,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteRedigerDeltakereGeneralforsamlingKontroller {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormArray<DeltakerSkjemaType>([])
    }

    vedGaaTilRepresentasjonSteg = output();

    /** skjema med møtedeltakere, en rad per møtedeltaker */
    deltakereSkjema = input.required<DeltakereGfSkjemaType>();
    /** Skjema med aksjeeierrepresentasjoner, en rad per aksjeeier. */
    representasjonerSkjema = input.required<GfRedigerRepresentasjonerSkjemaType>();
    skalSignereValgSynlig = input(true, {transform: booleanAttribute});

    /** Liste med {@link DeltakerSomRepresentererAksjeeiere}, ett innslag per unike medlem/invitasjon i {@link #representasjonerSkjema}, med liste over aksjonærene disse representerer */
    deltakereSomRepresentererAksjeeiere = toSignal(this.beregnDeltakereSomRepresentererAksjeeiere(), {initialValue: []});
    /** Liste med {@link DeltakerSkjemaType}, ett innslag per medlem {@link #deltakereSkjema} som ikke representerer noen aksjeeier */
    medlemmerSomIkkeRepresentererAksjeeier = computed(() => this.beregnMedlemmerSomIkkeRepresentererAksjeeier());

    antallTotalt = computed(() => this.medlemmerSomIkkeRepresentererAksjeeier().length + this.deltakereSomRepresentererAksjeeiere().length);
    antallTotaltValgt = computed(() => this.antallSomIkkeReprAksjeeierValgt() + this.deltakereSomRepresentererAksjeeiere().length);

    antallSomIkkeReprAksjeeierValgt = toSignal(this.skjemaendringer().pipe(
      map(() => this.medlemmerSomIkkeRepresentererAksjeeier()),
      map(rader => rader.filter(rad => rad.value.valgt)),
      map(valgteRader => valgteRader.length)
    ), {initialValue: 0});
    alleSomIkkeReprAksjeeierValgt = computed(() => this.antallSomIkkeReprAksjeeierValgt() == this.medlemmerSomIkkeRepresentererAksjeeier().length && this.medlemmerSomIkkeRepresentererAksjeeier().length > 0);
    ingenSomIkkeReprAksjeeierValgt = computed(() => this.antallSomIkkeReprAksjeeierValgt() == 0);

    antallAksjeeiereUtenRepresentantTildelt = toSignal(skjemaendringerSomObserverbar(this.representasjonerSkjema).pipe(
      map(rader => rader.filter(rad => rad.erNyInvitasjon ? erNil(rad.nyInvitasjonForAksjeeier) : erNil(rad.medlem))),
      map(raderUtenRepresentantTildelt => raderUtenRepresentantTildelt.length)
    ), {initialValue: 0});

    constructor() {
    }

    endreAlleSomIkkeReprAksjeeier() {
        const erValgt = !this.alleSomIkkeReprAksjeeierValgt();
        this.medlemmerSomIkkeRepresentererAksjeeier()
          .forEach(kontroll => kontroll.controls.valgt.patchValue(erValgt));
    }

    gaaTilRepresentasjonSteg() {
        this.vedGaaTilRepresentasjonSteg.emit();
    }

    // private hjelpemetoder

    /** Returnerer en strøm av modeller som reberegnes hver gang innslagene i {@link #representasjonerSkjema} eller {@link #deltakereSkjema} endres */
    private beregnDeltakereSomRepresentererAksjeeiere(): Observable<DeltakerSomRepresentererAksjeeiere[]> {
        return this.endringerISkjemaer().pipe(
          //representasjonerSkjema eller deltakereSkjema er endret - vi reberegner - representasjoner der hverken medlem eller nyInvitasjonForAksjeeier er satt har blitt nå blitt filtrert bort
          map(() => this.representasjonerSkjema().controls),
          //filtrerer bort representasjoner som hverken har medlem eller nyInvitasjonForAksjeeier med epost satt (dvs at aksjeeier ikke har blitt tildelt en representant)
          //kreves av senerer kall til somDeltakerSomRepresentererAksjeeiere(..)
          map(representasjoner => representasjoner.filter(representasjon => erRepresentantForAksjeeierAngitt(representasjon.value))),
          map(representasjoner => {
              //reduserer aksjeeierrepresentasjonene i representasjonerSkjema til en liste med medlem/invitasjon og hvilke aksjeeiere de representerer
              //for invitasjoner bruker vi angitt epost som nøkkel slik at invitasjoner med lik epostadresse slås sammen til én
              const deltakere = representasjoner
                .map(representasjon => this.somDeltakerSomRepresentererAksjeeiere(representasjon))
                .filter(representasjon => erIkkeNil(representasjon)) //somDeltakerSomRepresentererAksjeeiere() kan returnere null
                .reduce((medlemmerMedAksjeeiere, deltaker) => {
                    let nøkkel = this.medlemIdEllerInvitasjonEpost(deltaker);
                    const deltakerSomReprAksjeeiere = medlemmerMedAksjeeiere[nøkkel] || (medlemmerMedAksjeeiere[nøkkel] = deltaker);
                    deltakerSomReprAksjeeiere.aksjeeiere = distinkte([...deltakerSomReprAksjeeiere.aksjeeiere, ...deltaker.aksjeeiere], (a, b) => a.id == b.id);
                    return medlemmerMedAksjeeiere;
                }, {} as Record<MedlemId | Epost, DeltakerSomRepresentererAksjeeiere>);

              return Object.values(deltakere);
          })
        );
    }

    private medlemIdEllerInvitasjonEpost(deltaker:DeltakerSomRepresentererAksjeeiere){
       return erDeltakerSkjema(deltaker.deltakerEllerInvitasjon) ?
          deltaker.deltakerEllerInvitasjon.value.medlem!.id :
          deltaker.deltakerEllerInvitasjon.value.nyInvitasjonSkjema!.epost!;
    }

    /** Observerbar som leverer når aksjeeier->medlem/invitasjon knytninger i {@link #representasjonerSkjema}
     * eller medlemmer i {@link #deltakereSkjema} er endret */
    private endringerISkjemaer(): Observable<any> {
        return this.skjemaendringer().pipe(
          //filtrerer bort representasjoner som hverken har medlem eller nyInvitasjonForAksjeeier med epost satt (dvs at aksjeeier ikke har blitt tildelt en representant)
          //kreves av senerer kall til medlemIdEllerInvitasjonEpost(..)
          map(representasjonerOgDeltakere => ({
              representasjoner: representasjonerOgDeltakere.representasjoner
                .filter(representasjon => erRepresentantForAksjeeierAngitt(representasjon))
                .filter(representasjon => representasjon.erNyInvitasjon ? erIkkeTomStreng(representasjon.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.epost) : true),
              deltakere: representasjonerOgDeltakere.deltakere
          })),
          //Vi ønsker kun å levere nye verdier dersom aksjeeier->medlem/invitasjon knytninger i representasjonerSkjema eller medlemmer i deltakereSkjema er endret,
          //(og ikke dersom feks 'inkludert' eller 'skal signere' feltene endres)
          //Oppnår dette ved å lage en liste med ider for de respektive listene (representasjonerSkjema/deltakereSkjema) og reagere på endringer i denne listen
          map(representasjonerOgDeltakere =>
            [...(representasjonerOgDeltakere.representasjoner.map(representasjon => representasjon.aksjeeier!.id + ',' + medlemIdEllerInvitasjonId(representasjon))),
                ...(representasjonerOgDeltakere.deltakere.map(deltaker => deltaker.medlem!.id))]
              .join(',')),
          distinctUntilChanged()
        );
    }

    private somDeltakerSomRepresentererAksjeeiere(representasjon: AksjeeierRepresentasjonSkjemaType): DeltakerSomRepresentererAksjeeiere | null {
        const deltakerEllerInvitasjon = representasjon.value.erNyInvitasjon ?
          representasjon.controls.nyInvitasjonForAksjeeier :
          this.deltakereSkjema().controls.find(deltaker => deltaker.value.medlem!.id == representasjon.value.medlem?.id);

        if (erNil(deltakerEllerInvitasjon)) {
            //representasjon.medlem er satt men det aktuelle medlemmet er ikke å finne i deltakereSkjema
            //denne situasjonen skal ikke oppstå, men kan teoretisk oppstå dersom deltakereSkjema er basert på et annet sett av medlemmer enn det som er benyttet for å populer representasjon.medlem
            console.warn('Fant ingen deltaker eller invitasjon for aksjeeier med id', representasjon.value.aksjeeier!.id);
            return null;
        }
        return {
            medlemIdEllerInvitasjonId: medlemIdEllerInvitasjonId(representasjon.value)!,
            deltakerEllerInvitasjon: deltakerEllerInvitasjon,
            aksjeeiere: [representasjon.value.aksjeeier!]
        }
    }

    private skjemaendringer() {
        return combineLatest({representasjoner: skjemaendringerSomObserverbar(this.representasjonerSkjema), deltakere: skjemaendringerSomObserverbar(this.deltakereSkjema)});
    }

    private beregnMedlemmerSomIkkeRepresentererAksjeeier(): DeltakerSkjemaType[] {
        const medlemmerSomRepresentererAksjeeiere = this.deltakereSomRepresentererAksjeeiere()
          .map(representasjon => representasjon.deltakerEllerInvitasjon)
          .filter(deltakerEllerInvitasjon => erDeltakerSkjema(deltakerEllerInvitasjon))
          .map(deltaker => deltaker.value.medlem!.id);

        return this.deltakereSkjema().controls
          .filter(medlemSkjema => !medlemmerSomRepresentererAksjeeiere.includes(medlemSkjema.value.medlem!.id));
    }
}

//eksportert for testformål
export interface DeltakerSomRepresentererAksjeeiere {
    medlemIdEllerInvitasjonId: MedlemId | Epost;
    deltakerEllerInvitasjon: DeltakerSkjemaType | MedlemInvitasjonsskjemaForAksjeeierSkjemaType;
    aksjeeiere: AksjeeierEnkelModell[];
}

export function erDeltakerSkjema(deltakerEllerInvitasjon: DeltakerSkjemaType | MedlemInvitasjonsskjemaForAksjeeierSkjemaType): deltakerEllerInvitasjon is DeltakerSkjemaType {
    return !erInvitasjonSkjema(deltakerEllerInvitasjon);
}

export function erInvitasjonSkjema(deltakerEllerInvitasjon: DeltakerSkjemaType | MedlemInvitasjonsskjemaForAksjeeierSkjemaType): deltakerEllerInvitasjon is MedlemInvitasjonsskjemaForAksjeeierSkjemaType {
    return GeneralforsamlingRedigerRepresentasjonerKontroller.erInvitasjonForAksjeeierSkjema(deltakerEllerInvitasjon);
}
